import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'

import { useFeatures } from '@jotta/grpc-connect-client/config'
import { useUsername } from '@jotta/grpc-connect-client/customer'

import { usePhotoStore } from '../../store/PhotoContext'
import { MediaCarouselContainer } from '../MediaCarousel/MediaCarouselContainer'
import { NoTimelinePhotos } from '../NoTimelinePhotos/NoTimelinePhotos'
import type { PhotoActionContextType } from '../PhotoActions/PhotoActionContext.types'
import { PhotoSelectionActions } from '../PhotoActions/PhotoSelectionActions'
import { SelectionManager } from '../SelectionManager/SelectionManager'
import { TimelineScrubber } from '../TimelineScrubber/TimelineScrubber'

import { TimelineV2 } from './timelinev2/TimelineV2'
import {
  hiddenTimelineCollection,
  type TimelinePhoto,
  timelineCollection,
  useCollectionRef,
} from './timelinev2/useTimelineStream'
import { Timeline } from './Timeline'
import { useAddPhotoMode } from './useAddPhotoMode'
import type { GalleryRef } from './useGroupByDayAndRow'

export const TimelineGallery = observer<{
  mediaId?: string
  hidden?: boolean
}>(function TimelineGallery({ mediaId, hidden = false }) {
  const ref = useRef<GalleryRef>(null)
  const photoStore = usePhotoStore()
  const features = useFeatures()
  const username = useUsername()

  const [addPhotoMode] = useAddPhotoMode()
  const actionContext: PhotoActionContextType = addPhotoMode
    ? 'ADD_PHOTOS_TO_ALBUM_SELECTED_PHOTOS'
    : hidden
      ? 'TIMELINE_HIDDEN_PHOTOS_SELECTED_PHOTOS'
      : 'TIMELINE_PHOTOS_SELECTED_PHOTOS'
  const showSimilarSearch = !hidden && features.photoSearch

  const [photosV2, setPhotosV2] = useState<TimelinePhoto[]>([])
  const photoIdsV2 = photosV2.map(photo => photo.id)
  const [navigateToV2, setNavigateToV2] = useState<
    { id: string; timestamp: bigint } | undefined
  >()
  useEffect(() => {
    if (!mediaId) {
      setNavigateToV2(undefined)
    }
  }, [mediaId, setNavigateToV2])

  const { timelineV2 } = useFeatures()
  const timelineType = hidden ? hiddenTimelineCollection : timelineCollection
  const collectionRef = useCollectionRef(timelineType)

  if (timelineV2) {
    return (
      <>
        <TimelineV2
          type={timelineType}
          onPhotos={setPhotosV2}
          navigateTo={navigateToV2?.timestamp}
        />
        <SelectionManager
          addPhotoMode={addPhotoMode}
          selectables={photosV2}
          actionContext={actionContext}
        >
          <PhotoSelectionActions collectionRef={collectionRef} />
        </SelectionManager>
        <MediaCarouselContainer
          collectionRef={collectionRef}
          mediaId={mediaId}
          ids={photoIdsV2}
          previewItems={photosV2}
          showSimilarSearch={showSimilarSearch}
          onNavigate={setNavigateToV2}
        />
      </>
    )
  }

  const { isLoading, isHidden, photos, photoIds } = photoStore.timeline
  const showWaitinglistBanners = !hidden && features.photoSearch && !isLoading

  return (
    <>
      {!isLoading && !photos.length ? (
        <NoTimelinePhotos type={timelineType} />
      ) : (
        <Timeline
          ref={ref}
          photos={photos}
          showWaitinglistBanners={showWaitinglistBanners}
        />
      )}
      {photos.length > 0 && <TimelineScrubber />}
      <SelectionManager
        addPhotoMode={addPhotoMode}
        selectables={photos}
        actionContext={actionContext}
      >
        <PhotoSelectionActions collectionRef={collectionRef} />
      </SelectionManager>
      <MediaCarouselContainer
        collectionRef={collectionRef}
        mediaId={mediaId}
        ids={photoIds}
        showSimilarSearch={showSimilarSearch}
      />
    </>
  )
})
